import { useState } from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';

import { cn } from '../../lib/utils';
import type { SliderProps } from './types';

function Slider({
  value,
  defaultValue = 0,
  onValueChange,
  showValueLabel = true,
  valueLabelFormat = (v) => `${v}`,
  marks = false,
  error = false,
  className,
  min = 0,
  max = 100,
  step = 1,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  ...props
}: SliderProps) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<number>(defaultValue);
  const currentValue = isControlled ? value : internalValue;

  const handleChange = (val: number[]) => {
    if (!isControlled) setInternalValue(val[0]);
    onValueChange?.(val[0]);
  };

  return (
    <SliderPrimitive.Root
      data-testid='slider-root'
      className={cn(
        'eye:relative eye:flex eye:w-full eye:cursor-pointer eye:touch-none eye:items-center eye:select-none eye:aria-disabled:cursor-not-allowed eye:aria-disabled:opacity-50 eye:data-disabled:cursor-not-allowed',
        className,
      )}
      value={[currentValue]}
      onValueChange={handleChange}
      min={min}
      max={max}
      step={step}
      {...props}
    >
      <SliderPrimitive.Track
        data-testid='slider-track'
        className={cn(
          'eye:relative eye:h-12 eye:w-full eye:grow eye:overflow-hidden eye:rounded-full eye:aria-disabled:pointer-events-none eye:data-disabled:pointer-events-none',
          error ? 'eye:bg-red-20' : 'eye:bg-grey-20',
        )}
      >
        <SliderPrimitive.Range
          data-testid='slider-range'
          className={cn(
            'eye:absolute eye:h-full',
            error ? 'eye:bg-red-100' : 'eye:bg-blue-100',
          )}
        />
      </SliderPrimitive.Track>

      {marks && (
        <div
          data-testid='slider-marks'
          className='eye:absolute eye:flex eye:w-full eye:grow eye:items-center eye:justify-between eye:px-12'
        >
          {Array.from(
            { length: Math.floor((max - min) / step) + 1 },
            (_, i) => min + i * step,
          ).map((_, index) => (
            <div
              className='eye:h-4 eye:w-1 eye:rounded-full eye:bg-white'
              key={index}
            ></div>
          ))}
        </div>
      )}

      <SliderPrimitive.Thumb
        data-testid='slider-thumb'
        className={cn(
          'eye:group eye:relative eye:block eye:h-[21px] eye:w-[21px] eye:rounded-full eye:border-2 eye:bg-white eye:outline-black eye:transition-colors eye:focus:outline-2 eye:focus:outline-offset-2 eye:focus-visible:outline-2 eye:focus-visible:outline-offset-2 eye:data-disabled:pointer-events-none eye:data-disabled:cursor-not-allowed',
          error ? 'eye:border-red-100' : 'eye:border-blue-100',
        )}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-valuetext={valueLabelFormat(currentValue)}
        aria-invalid={error}
      >
        {showValueLabel && !error && (
          <div
            className={cn(
              'eye:body-m',
              'eye:absolute eye:-top-48 eye:left-1/2 eye:-translate-x-1/2 eye:rounded-md',
              error ? 'eye:bg-red-100' : 'eye:bg-blue-100',
              'eye:p-4xs eye:opacity-0 eye:transition-opacity eye:group-hover:opacity-100',
              'eye:group-focus:opacity-100 eye:[&]:text-white',
              'eye:w-max eye:min-w-[40px] eye:text-center',
            )}
          >
            {valueLabelFormat(currentValue)}
          </div>
        )}
      </SliderPrimitive.Thumb>
    </SliderPrimitive.Root>
  );
}

export { Slider };
